// client/src/pages/admin/DeskNotes.tsx
// Per-card Notes for the Desks & Assignments / Admin Team Dashboard boards — the same
// expandable note editor the Dev Team board has, sized for the narrow board cards.
// Open/closed state is owned by the board (a card re-mounts when it changes column, and
// moving one to Completed opens its editor so a note can be added right then).

import React, { useEffect, useState } from 'react';
import { StickyNote } from 'lucide-react';

export const MAX_NOTES = 4000;

interface Props {
  notes: string;
  updatedBy: string;
  updatedAt: string;
  open: boolean;
  onToggle: () => void;
  /** Persist the trimmed text ('' clears). Rejects with a user-facing message on failure. */
  onSave: (notes: string) => Promise<void>;
  /** Shown above the editor, e.g. the "marked completed — add a note?" nudge. */
  hint?: string;
  /** Pretty-prints an editor's email. */
  nameOf: (email: string) => string;
}

export default function DeskNotes({ notes, updatedBy, updatedAt, open, onToggle, onSave, hint, nameOf }: Props) {
  const [draft, setDraft] = useState(notes);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hasNotes = notes.trim() !== '';

  // Each time the editor opens, start from the saved text.
  useEffect(() => { if (open) { setDraft(notes); setErr(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setErr(null); setSaving(true);
    try { await onSave(draft.trim()); onToggle(); }
    catch (e: any) { setErr(e?.message || 'Could not save notes.'); }
    finally { setSaving(false); }
  };

  const edited = updatedBy
    ? `Last edited by ${nameOf(updatedBy)}${updatedAt && !Number.isNaN(Date.parse(updatedAt)) ? ` · ${new Date(updatedAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}` : ''}`
    : '';

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button" onClick={onToggle} title={hasNotes ? 'View or edit notes' : 'Add a note'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: open ? 'rgba(255,255,255,.06)' : 'none', border: '1px solid var(--border)', color: hasNotes ? 'var(--accent)' : 'var(--muted)', borderRadius: 8, padding: '3px 9px', fontSize: 10.5, cursor: 'pointer' }}
      >
        <StickyNote size={11} /> Notes{hasNotes ? ' •' : ''}
      </button>

      {/* Saved note, visible at a glance while the editor is closed */}
      {!open && hasNotes && (
        <div
          title={`${notes}${edited ? `\n\n${edited}` : ''}`}
          style={{ marginTop: 5, fontSize: 10.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          className="muted"
        >
          {notes}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {hint && <div style={{ fontSize: 10.5, color: '#57d38c', fontWeight: 600 }}>{hint}</div>}
          <textarea
            value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} maxLength={MAX_NOTES} autoFocus
            placeholder="Notes for this item…"
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(0,0,0,.15)', color: 'inherit', fontSize: 12, fontFamily: 'inherit', lineHeight: 1.45, padding: '7px 9px' }}
          />
          {err && <span style={{ color: '#f87171', fontSize: 10.5 }}>{err}</span>}
          {edited && <span className="muted" style={{ fontSize: 10 }}>{edited}</span>}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onToggle} disabled={saving} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 8, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>
              {hint ? 'Skip' : 'Cancel'}
            </button>
            <button type="button" onClick={save} disabled={saving} style={{ background: saving ? 'var(--border)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
